/**
 * 재생이 **멈춘 채로 아무 말도 하지 않는 것**을 잡는 시계.
 *
 * 🚨 2026-08-18 prod 실측 — 감상회에서 재생을 눌렀는데 아무 일도 일어나지 않았다.
 *    계측해 보니 `readyState 0` · `networkState 2`(LOADING) · `currentTime 0` 이 10초 넘게
 *    그대로였고, 이벤트는 `loadstart` → `stalled` 뿐 **`error` 는 끝내 오지 않았다.**
 *    (그날 R2 서명 URL 에 대한 미디어 요청 하나가 503 을 받았고, 같은 주소를 그냥 받으면
 *     200 이었다 — 간헐적이다.)
 *
 * 🔑 그래서 `onError` 만으로는 부족하다. 실패에는 «에러가 오는 실패» 와 «아무것도 안 오는
 *    실패» 두 가지가 있는데, 후자는 시간으로만 잡힌다. PR #123 이 앞의 절반을 고쳤고
 *    이 파일이 나머지 절반이다.
 *
 * 🔑 «바이트가 오고 있는 느린 회선» 을 실패로 몰지 않는다 — 진행의 증거가 하나라도 오면
 *    시계를 처음부터 다시 센다. 학교 와이파이에서 없던 빨강을 만들면 안 된다.
 */

/** 재생을 누른 뒤 **진행의 증거가 하나도 없을 때** 실패로 판정하기까지의 시간. */
export const PLAYBACK_STALL_TIMEOUT_MS = 10_000;

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — 이 지점부터는 «틀어졌다» 고 본다. */
const HAVE_CURRENT_DATA = 2;

/**
 * 진행의 증거로 인정하는 사건들.
 *
 * 🚨 `stalled` · `suspend` · `waiting` 은 여기 넣지 않는다 — 그것들은 «진행» 이 아니라
 *    «진행이 없다» 는 통보다. 넣으면 시계가 영원히 되감겨 아무것도 못 잡는다.
 */
const PROGRESS_EVENTS = [
  "progress",
  "loadedmetadata",
  "loadeddata",
  "canplay",
  "playing",
  "timeupdate",
] as const;

/** 이 시계가 미디어 엘리먼트에게 요구하는 전부 — 테스트에서 가짜로 세울 수 있을 만큼 작다. */
export interface StallWatchTarget {
  readonly readyState: number;
  readonly currentTime: number;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/** 이미 소리(그림)가 나오고 있는가 — 나오고 있으면 더 볼 것이 없다. */
function alreadyPlaying(media: StallWatchTarget): boolean {
  return media.readyState >= HAVE_CURRENT_DATA || media.currentTime > 0;
}

/**
 * `media` 가 `timeoutMs` 안에 재생을 시작하지 못하면 `onStall` 을 **한 번만** 부른다.
 *
 * @returns 감시를 그만두는 함수. 작품을 넘기거나 화면을 떠날 때 반드시 부른다.
 */
export function watchPlaybackStall(
  media: StallWatchTarget,
  onStall: () => void,
  timeoutMs: number = PLAYBACK_STALL_TIMEOUT_MS,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    for (const name of PROGRESS_EVENTS) media.removeEventListener(name, onProgress);
  };

  const arm = (): void => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      // 시계가 다 돌았는데 그 사이 재생이 시작됐을 수도 있다 — 마지막으로 한 번 더 본다.
      if (alreadyPlaying(media)) {
        stop();
        return;
      }
      stop();
      onStall();
    }, timeoutMs);
  };

  function onProgress(): void {
    if (stopped) return;
    // 소리가 나기 시작했으면 감시 자체를 끝낸다. 아직이면 «살아는 있다» 로 보고 다시 센다.
    if (alreadyPlaying(media)) stop();
    else arm();
  }

  if (alreadyPlaying(media)) return () => undefined;
  for (const name of PROGRESS_EVENTS) media.addEventListener(name, onProgress);
  arm();
  return stop;
}
